//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useActiveLoopContext } from '../../ActiveLoopContext';

const routeLabels: Record<string, string> = {
  '/': 'dashboard',
  '/dashboard': 'teamspace-dashboards',
  '/plugin': 'plugins',
  '/integration': 'integration',
  '/search': 'search-results',
  '/documentation': 'documentation',
  '/user-administration': 'user-administration',
  '/announcements': 'announcements',
  '/teamspace': 'create-teamspace',
  '/loop': 'Create-Loop',
  '/user-groups': 'userGroups.title',
  '/wiki': 'wiki.tabLabel',
};

const getPageTitle = (pathname: string, t: any): string => {
  // Check exact match first
  if (routeLabels[pathname]) {
    const key = routeLabels[pathname];
    // Use i18n with fallback English labels
    const fallbacks: Record<string, string> = {
      dashboard: 'Dashboard',
      'teamspace-dashboards': 'Dashboards',
      plugins: 'Plugins',
      integration: 'Installed Integrations',
      'search-results': 'Search Results',
      documentation: 'Documentation',
      'user-administration': 'User Administration',
      announcements: 'Announcements',
      'create-teamspace': 'Create TeamSpace',
      'Create-Loop': 'Create Loop',
      'userGroups.title': 'User Group Management',
      'wiki.tabLabel': 'Wiki',
    };
    return t(key, fallbacks[key] || '');
  }

  // Extract base path for dynamic routes (e.g., /loop/123 → /loop)
  const basePath = pathname.split('/').slice(0, 2).join('/') || '/';
  if (routeLabels[basePath]) {
    const key = routeLabels[basePath];
    const fallbacks: Record<string, string> = {
      dashboard: 'Dashboard',
      'teamspace-dashboards': 'Dashboards',
      plugins: 'Plugins',
      integration: 'Installed Integrations',
      'search-results': 'Search Results',
      documentation: 'Documentation',
      'user-administration': 'User Administration',
      announcements: 'Announcements',
      'create-teamspace': 'Create TeamSpace',
      'Create-Loop': 'Create Loop',
      'userGroups.title': 'User Group Management',
      'user-groups': 'User Groups', // Added fallback for user-groups
    };
    return t(key, fallbacks[key] || '');
  }

  return '';
};

const Breadcrumb: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { activeLoopName, isLoopContextActive } = useActiveLoopContext();
  let pageTitle = getPageTitle(pathname, t);

  // When loop context is active and on dashboard page, show loop-scoped title
  if (isLoopContextActive && activeLoopName && pathname === '/dashboard') {
    pageTitle = `${activeLoopName} ${t('dashboards')}`;
  }

  return (
    <div
      className="flex min-h-14 flex-col justify-center border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-carbon-darkSurface"
      id="id-breadcrumb-container"
      data-testid="breadcrumb-container"
    >
      <nav className="flex items-center">
        <Link
          to="/"
          className="font-roboto text-[14px] font-normal leading-[34px] tracking-[0.16px] text-breadcrumb-link hover:underline dark:text-brand-linkLight"
        >
          DevOps Loop
        </Link>
      </nav>
      {pageTitle && (
        <h1 className="font-roboto text-[20px] font-bold leading-[14px] text-breadcrumb-title dark:text-gray-100">
          {pageTitle}
        </h1>
      )}
    </div>
  );
};

export default Breadcrumb;
